import { useState, useRef } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { PageShell, PageHero } from "@/components/PageShell";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { UploadCloud, X } from "lucide-react";

// Images
import heroImage from "@/assets/hero-contact.avif";
import { Reveal } from "@/components/Reveal";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AsYouType, CountryCode } from "libphonenumber-js";

const COUNTRIES = [
  { code: "BR", name: "Brasil", dialCode: "+55", flag: "🇧🇷", placeholder: "(11) 99999-9999" },
  { code: "US", name: "Estados Unidos", dialCode: "+1", flag: "🇺🇸", placeholder: "(201) 555-0123" },
  { code: "GB", name: "Reino Unido", dialCode: "+44", flag: "🇬🇧", placeholder: "7911 123456" },
  { code: "FR", name: "França", dialCode: "+33", flag: "🇫🇷", placeholder: "06 12 34 56 78" },
  { code: "DE", name: "Alemanha", dialCode: "+49", flag: "🇩🇪", placeholder: "0151 23456789" },
  { code: "ES", name: "Espanha", dialCode: "+34", flag: "🇪🇸", placeholder: "612 345 678" },
  { code: "IT", name: "Itália", dialCode: "+39", flag: "🇮🇹", placeholder: "312 345 6789" },
  { code: "PT", name: "Portugal", dialCode: "+351", flag: "🇵🇹", placeholder: "912 345 678" },
  { code: "AR", name: "Argentina", dialCode: "+54", flag: "🇦🇷", placeholder: "(11) 1234-5678" },
  { code: "CL", name: "Chile", dialCode: "+56", flag: "🇨🇱", placeholder: "9 1234 5678" },
  { code: "IN", name: "Índia", dialCode: "+91", flag: "🇮🇳", placeholder: "91234 56789" },
  { code: "JP", name: "Japão", dialCode: "+81", flag: "🇯🇵", placeholder: "090-1234-5678" },
];

export const Route = createFileRoute("/$lang/careers")({
  head: () => ({
    meta: [
      { title: i18n.t("careers:title", "Careers") + " — BIZU Space" },
      {
        name: "description",
        content: i18n.t("careers:metaDesc",
          "Join BIZU Space and help build the future of space technology. We value culture, character, and hard work.",
        ),
      },
      { property: "og:title", content: i18n.t("careers:title", "Careers") + " — BIZU Space" },
      { property: "og:description", content: i18n.t("careers:metaDesc", "Join BIZU Space and help build the future of space technology. We value culture, character, and hard work.") },
      { property: "og:url", content: "/careers" },
    ],
    links: [{ rel: "canonical", href: "https://bizu.space/careers" }],
  }),
  component: Careers,
});

function Careers() {
  const { t } = useTranslation("careers");
  const [submitted, setSubmitted] = useState(false);
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState<CountryCode>("BR");
  const [isSending, setIsSending] = useState(false);
  const [formError, setFormError] = useState("");

  const [cvFile, setCvFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const formatPhone = (value: string, currentCountry: CountryCode) => {
    if (currentCountry === "BR") {
      const numbers = value.replace(/\D/g, "").slice(0, 11);
      if (!numbers) return "";
      if (numbers.length <= 10) {
        return numbers.replace(/^(\d{2})(\d)/, "($1) $2").replace(/(\d{4})(\d)/, "$1-$2");
      }
      return numbers.replace(/^(\d{2})(\d)/, "($1) $2").replace(/(\d{5})(\d)/, "$1-$2");
    }

    const formatter = new AsYouType(currentCountry);
    return formatter.input(value);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith('.pdf')) {
        setFormError(t("errors.onlyPdf", "Only PDF files are allowed."));
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setFormError(t("errors.maxSize", "File size must be under 10 MB."));
        return;
      }
      setFormError("");
      setCvFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith('.pdf')) {
        setFormError(t("errors.onlyPdf", "Only PDF files are allowed."));
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setFormError(t("errors.maxSize", "File size must be under 10 MB."));
        return;
      }
      setFormError("");
      setCvFile(file);

      // Atualizar o input nativo para o form conseguir enviar
      if (fileInputRef.current) {
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        fileInputRef.current.files = dataTransfer.files;
      }
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    if (!cvFile) {
      event.preventDefault();
      setFormError(t("errors.noFile", "Please upload your CV."));
      return;
    }
    // Allow the browser to submit the form natively to formsubmit.co via the hidden iframe
    setIsSending(true);
    setFormError("");

    // Simulate success after a short delay since we can't read the iframe response directly due to cross-origin
    setTimeout(() => {
      setSubmitted(true);
      setIsSending(false);
    }, 1500);
  };

  const missionItems = [
    {
      num: t("vision.num", "01"),
      title: t("vision.title", "VISION"),
      desc: t("vision.desc", "To be internationally recognized as a trusted provider of reliable aerospace products and services"),
    },
    {
      num: t("mission.num", "02"),
      title: t("mission.title", "MISSION"),
      desc: t("mission.desc", "To create a bridge between Brazil and space"),
    },
    {
      num: t("values.num", "03"),
      title: t("values.title", "VALUES"),
      desc: t("values.desc", "We value culture and character. Passion for space, committed execution, desire for challenges and initiative. Hard work pays off."),
    }
  ];

  return (
    <PageShell>
      <PageHero
        title={t("heroTitle", "Ready for a challenging space career?")}
        image={heroImage}
      >
        <button
          onClick={() => {
            document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth' });
          }}
          className="inline-flex h-12 md:h-14 items-center justify-center rounded-full bg-accent px-8 text-sm md:text-base font-semibold text-black transition hover:bg-accent/80"
        >
          {t("heroDesc", "Apply Now")}
        </button>
      </PageHero>

      {/* Mission, Vision, Values */}
      <section className="container-bizu py-20 lg:py-32 space-y-16">
        <div className="divide-y divide-white/10 border-y border-white/10">
          {missionItems.map((item, index) => (
            <Reveal key={index} effect="up" className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 md:gap-24 py-10 items-center group">
              <div className="flex gap-8 items-start">
                <span className="text-sm font-bold text-white mt-3 group-hover:text-accent transition-colors">{item.num}</span>
                <h2 className="display text-4xl md:text-6xl tracking-widest uppercase">{item.title}</h2>
              </div>
              <div className="md:text-right">
                <p className="text-xl md:text-2xl text-white/60 leading-tight">
                  {item.desc}
                </p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal effect="scale" className="text-center pt-8">
          <h3 className="display text-2xl md:text-4xl text-accent">
            {t("quote", "Rocket technology is hard. That’s why we do it.")}
          </h3>
        </Reveal>
      </section>

      {/* Application Form */}
      <section id="application-form" className="container-bizu pb-20 lg:pb-32">
        <div className="bg-black/40 border border-border rounded-3xl p-8 md:p-14 shadow-2xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-10 text-white">
            {t("formTitle", "Apply now")}
          </h2>

          {submitted ? (
            <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
              <h3 className="text-3xl font-bold text-accent">{t("successTitle", "Application sent successfully!")}</h3>
              <p className="mt-4 max-w-md text-muted-foreground text-lg">
                {t("successDesc", "Thank you for applying to BIZU Space. Our team will review your application and get back to you.")}
              </p>
            </div>
          ) : (
            <>
              <iframe
                name="hidden_iframe_careers"
                id="hidden_iframe_careers"
                style={{ display: "none" }}
              ></iframe>
              <form
                action="https://formsubmit.co/daf2b75e64fb846c63068f320fa8dc94"
                method="POST"
                encType="multipart/form-data"
                target="hidden_iframe_careers"
                onSubmit={handleSubmit}
                className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-12"
              >
                <input type="hidden" name="_subject" value="New Job Application - BIZU Space" />
                <input type="hidden" name="_captcha" value="false" />
                <input type="hidden" name="_template" value="table" />

                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="mb-2 flex gap-1 text-sm font-bold text-white/80">
                        {t("labels.firstName", "First Name")} <span className="text-red-500">*</span>
                      </label>
                      <input
                        name="firstName"
                        required
                        type="text"
                        placeholder={t("placeholders.firstName", "First Name")}
                        className="h-12 w-full rounded-md border border-white/10 bg-white/5 px-4 text-white placeholder:text-white/30 outline-none focus:border-accent transition"
                      />
                    </div>
                    <div>
                      <label className="mb-2 flex gap-1 text-sm font-bold text-white/80">
                        {t("labels.lastName", "Last Name")} <span className="text-red-500">*</span>
                      </label>
                      <input
                        name="lastName"
                        required
                        type="text"
                        placeholder={t("placeholders.lastName", "Last Name")}
                        className="h-12 w-full rounded-md border border-white/10 bg-white/5 px-4 text-white placeholder:text-white/30 outline-none focus:border-accent transition"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="mb-2 flex gap-1 text-sm font-bold text-white/80">
                        {t("labels.phone", "Phone")} <span className="text-red-500">*</span>
                      </label>
                      <div className="flex w-full overflow-hidden rounded-md border border-white/10 bg-white/5 focus-within:border-accent transition">
                        <Select
                          value={country}
                          onValueChange={(val) => {
                            const newCountry = val as CountryCode;
                            setCountry(newCountry);
                            setPhone(formatPhone(phone, newCountry));
                          }}
                        >
                          <SelectTrigger className="h-12 w-[100px] rounded-none border-0 border-r border-white/10 bg-transparent shadow-none focus:ring-0">
                            <SelectValue placeholder="DDI" />
                          </SelectTrigger>
                          <SelectContent>
                            {COUNTRIES.map((c) => (
                              <SelectItem key={c.code} value={c.code}>
                                <span className="flex items-center gap-2">
                                  <span>{c.flag}</span>
                                  <span>{c.dialCode}</span>
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <input
                          name="phone"
                          required
                          type="tel"
                          value={phone}
                          onChange={(event) => setPhone(formatPhone(event.target.value, country))}
                          placeholder={COUNTRIES.find(c => c.code === country)?.placeholder || ""}
                          maxLength={16}
                          className="h-12 flex-1 bg-transparent px-4 text-white placeholder:text-white/30 outline-none"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="mb-2 flex gap-1 text-sm font-bold text-white/80">
                        {t("labels.email", "Email")} <span className="text-red-500">*</span>
                      </label>
                      <input
                        name="email"
                        required
                        type="email"
                        placeholder={t("placeholders.email", "you@example.com")}
                        className="h-12 w-full rounded-md border border-white/10 bg-white/5 px-4 text-white placeholder:text-white/30 outline-none focus:border-accent transition"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 flex gap-1 text-sm font-bold text-white/80">
                      {t("labels.linkedin", "LinkedIn Profile")} <span className="text-red-500">*</span>
                    </label>
                    <input
                      name="linkedin"
                      required
                      type="url"
                      placeholder={t("placeholders.linkedin", "LinkedIn Profile URL")}
                      className="h-12 w-full rounded-md border border-white/10 bg-white/5 px-4 text-white placeholder:text-white/30 outline-none focus:border-accent transition"
                    />
                  </div>

                  <div>
                    <label className="mb-2 flex gap-1 text-sm font-bold text-white/80">
                      {t("labels.excitement", "What excites you most about this job?")} <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="excitement"
                      required
                      rows={4}
                      placeholder={t("placeholders.excitement", "Enter Here")}
                      className="w-full resize-none rounded-md border border-white/10 bg-white/5 px-4 py-3 text-white placeholder:text-white/30 outline-none focus:border-accent transition"
                    />
                  </div>
                </div>

                <div className="flex flex-col">
                  <label className="mb-2 flex gap-1 text-sm font-bold text-white/80">
                    {t("labels.cv", "Upload your CV")} <span className="text-red-500">*</span>
                  </label>

                  <div
                    className={`flex-1 min-h-[250px] border-2 border-dashed rounded-md flex flex-col items-center justify-center p-6 text-center transition-colors cursor-pointer ${isDragging ? "border-accent bg-accent/20" :
                      cvFile ? "border-accent bg-accent/5" : "border-white/10 bg-white/5 hover:border-white/30 hover:bg-white/10"
                      }`}
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <input
                      type="file"
                      name="attachment"
                      ref={fileInputRef}
                      className="hidden"
                      accept="application/pdf"
                      onChange={handleFileChange}
                    />

                    {cvFile ? (
                      <div className="flex flex-col items-center">
                        <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center mb-4">
                          <UploadCloud className="w-8 h-8 text-accent" />
                        </div>
                        <p className="text-white font-medium break-all">{cvFile.name}</p>
                        <p className="text-white/50 text-sm mt-1">{(cvFile.size / 1024 / 1024).toFixed(2)} MB</p>
                        <button
                          type="button"
                          className="mt-4 text-xs font-bold uppercase tracking-wider text-white/60 hover:text-red-400 flex items-center gap-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCvFile(null);
                            if (fileInputRef.current) fileInputRef.current.value = '';
                          }}
                        >
                          <X className="w-3 h-3" /> {t("buttons.removeFile", "Remove File")}
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <UploadCloud className="w-10 h-10 text-white/40 mb-4" />
                        <p className="text-white/60 mb-4">
                          {t("cvDrop", "Drop files here or")}
                        </p>
                        <button type="button" className="h-12 rounded-full bg-accent px-8 text-sm font-semibold text-black transition hover:bg-accent/80 mt-2">
                          {t("cvSelect", "SELECT FILES")}
                        </button>
                      </div>
                    )}
                  </div>

                  <p className="text-white/40 text-xs mt-3">
                    {t("cvHint", "Accepted file types: pdf. Max. file size: 10 MB. Max. files: 1.")}
                  </p>

                  {formError && (
                    <p className="text-sm font-medium text-red-400 mt-4">{formError}</p>
                  )}

                  <div className="mt-auto pt-8">
                    <button
                      type="submit"
                      disabled={isSending}
                      className="h-14 w-full rounded-full bg-accent text-base font-semibold text-black transition hover:bg-accent/80 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isSending ? t("buttons.sending", "SENDING...") : t("buttons.submit", "SEND APPLICATION")}
                    </button>
                  </div>
                </div>
              </form>
            </>
          )}
        </div>
      </section>
    </PageShell>
  );
}
